import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { Loader2, RotateCcw, Maximize2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const COPY = {
  de: { loading: "Modell wird geladen…", surface: "Geschätzte Oberfläche", autorot: "Auto-Rotation", reset: "Zentrieren", note: "Vorschau ist eine Schätzung — die verbindliche Berechnung erfolgt im Werk anhand der Originaldatei." },
  fr: { loading: "Chargement du modèle…", surface: "Surface estimée", autorot: "Rotation auto", reset: "Centrer", note: "L'aperçu est une estimation — le calcul ferme est effectué à l'usine sur la base du fichier d'origine." },
  en: { loading: "Loading model…", surface: "Estimated surface", autorot: "Auto-rotate", reset: "Recenter", note: "Preview is an estimate — the binding calculation is performed at the plant from the original file." },
  it: { loading: "Caricamento modello…", surface: "Superficie stimata", autorot: "Rotazione auto", reset: "Centra", note: "L'anteprima è una stima — il calcolo vincolante avviene in stabilimento sul file originale." },
};

export default function StlViewer({ file }) {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const meshRef = useRef(null);
  const rotRef = useRef(true);
  const [loading, setLoading] = useState(true);
  const [surface, setSurface] = useState(null);
  const [autoRot, setAutoRot] = useState(true);
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.de;

  useEffect(() => {
    rotRef.current = autoRot;
  }, [autoRot]);

  useEffect(() => {
    if (!file || !mountRef.current) return;

    const mount = mountRef.current;
    const width = mount.clientWidth;
    const height = mount.clientHeight;

    const scene = new THREE.Scene();
    scene.background = null;
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(40, width / height, 0.1, 5000);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(width, height);
    mount.appendChild(renderer.domElement);

    // Lighting
    const ambient = new THREE.AmbientLight(0xffffff, 0.55);
    scene.add(ambient);
    const key = new THREE.DirectionalLight(0xffffff, 0.9);
    key.position.set(2, 3, 4);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0x66ccff, 0.4);
    rim.position.set(-3, 2, -2);
    scene.add(rim);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const loader = new STLLoader();
        const geometry = loader.parse(e.target.result);
        geometry.computeVertexNormals();
        geometry.center();
        geometry.computeBoundingSphere();

        // Compute approximate surface area (sum of triangle areas)
        const pos = geometry.attributes.position;
        let area = 0;
        const a = new THREE.Vector3(), b = new THREE.Vector3(), cv = new THREE.Vector3();
        const ab = new THREE.Vector3(), ac = new THREE.Vector3(), cross = new THREE.Vector3();
        for (let i = 0; i < pos.count; i += 3) {
          a.fromBufferAttribute(pos, i);
          b.fromBufferAttribute(pos, i + 1);
          cv.fromBufferAttribute(pos, i + 2);
          ab.subVectors(b, a);
          ac.subVectors(cv, a);
          cross.crossVectors(ab, ac);
          area += cross.length() * 0.5;
        }
        // STL units are arbitrary — assume mm; convert mm² → cm²
        setSurface((area / 100).toFixed(1));

        const mat = new THREE.MeshStandardMaterial({
          color: 0xc4c8cf,
          metalness: 0.85,
          roughness: 0.32,
          envMapIntensity: 1,
        });
        const mesh = new THREE.Mesh(geometry, mat);
        scene.add(mesh);
        meshRef.current = mesh;

        const radius = geometry.boundingSphere ? geometry.boundingSphere.radius : 50;
        camera.position.set(radius * 2.4, radius * 1.6, radius * 2.4);
        camera.lookAt(0, 0, 0);

        setLoading(false);
      } catch (err) {
        console.error("STL parse error", err);
        setLoading(false);
      }
    };
    reader.readAsArrayBuffer(file);

    let raf;
    const animate = () => {
      if (meshRef.current && rotRef.current) meshRef.current.rotation.y += 0.005;
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    // Mouse drag to rotate
    let isDown = false, sx = 0, sy = 0, baseRX = 0, baseRY = 0;
    const onDown = (e) => {
      isDown = true;
      sx = e.clientX || (e.touches && e.touches[0].clientX) || 0;
      sy = e.clientY || (e.touches && e.touches[0].clientY) || 0;
      if (meshRef.current) {
        baseRX = meshRef.current.rotation.x;
        baseRY = meshRef.current.rotation.y;
      }
      rotRef.current = false;
    };
    const onMove = (e) => {
      if (!isDown || !meshRef.current) return;
      const cx = e.clientX || (e.touches && e.touches[0].clientX) || 0;
      const cy = e.clientY || (e.touches && e.touches[0].clientY) || 0;
      meshRef.current.rotation.y = baseRY + (cx - sx) * 0.01;
      meshRef.current.rotation.x = baseRX + (cy - sy) * 0.01;
    };
    const onUp = () => { isDown = false; };
    const dom = renderer.domElement;
    dom.addEventListener("mousedown", onDown);
    dom.addEventListener("touchstart", onDown);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);

    // Resize handling
    const onResize = () => {
      if (!mount) return;
      const w = mount.clientWidth, h = mount.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
      window.removeEventListener("resize", onResize);
      dom.removeEventListener("mousedown", onDown);
      dom.removeEventListener("touchstart", onDown);
      try { mount.removeChild(dom); } catch (_) { /* ignore */ }
      renderer.dispose();
      if (meshRef.current) {
        meshRef.current.geometry.dispose();
        meshRef.current.material.dispose();
      }
    };
  }, [file]);

  const recenter = () => {
    if (meshRef.current) {
      meshRef.current.rotation.x = 0;
      meshRef.current.rotation.y = 0;
    }
  };

  return (
    <div data-testid="stl-viewer" className="mt-3 border border-white/10 bg-[#0B0B0D]">
      <div className="relative h-[280px] md:h-[320px]" ref={mountRef}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center text-zinc-400 text-sm gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            {c.loading}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-white/10">
        <div className="text-[12px] text-zinc-300">
          {surface ? (
            <>
              <span className="text-zinc-500 uppercase tracking-[0.14em] text-[10px] mr-2">
                {c.surface}
              </span>
              <span className="font-display text-base text-white">{surface} cm²</span>
            </>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setAutoRot((v) => !v)} data-testid="stl-autorot"
                  className={`px-2.5 py-1 text-[11px] uppercase tracking-[0.14em] border transition-colors ${
                    autoRot ? "bg-white text-black border-white" : "border-white/15 text-zinc-400 hover:text-white hover:border-white/40"
                  }`}>
            {c.autorot}
          </button>
          <button type="button" onClick={recenter} data-testid="stl-recenter"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] uppercase tracking-[0.14em] border border-white/15 text-zinc-400 hover:text-white hover:border-white/40 transition-colors">
            <RotateCcw className="w-3 h-3" strokeWidth={1.5} />
            {c.reset}
          </button>
        </div>
      </div>
      <div className="px-4 pb-3 text-[10px] text-zinc-500 leading-relaxed">{c.note}</div>
    </div>
  );
}
